import { supabase } from '../../config/supabase';

interface NotifySpaceMembersOptions {
  type: 'event_created' | 'event_updated' | 'event_deleted' | 'member_joined';
  spaceId: string;
  eventTitle?: string;
  taskTitle?: string;
  actorId: string;
  actorName: string;
}

interface SendNotificationEmailOptions {
  type: 'member_removed' | 'role_changed' | 'task_assigned';
  recipientEmail: string;
  recipientName?: string;
  actorName?: string;
  spaceName: string;
  eventTitle?: string;
  taskTitle?: string;
  oldRole?: string;
  newRole?: string;
}

/**
 * Notify all members of a space about an event change
 * Respects member notification preferences
 */
export const notifySpaceMembers = async (options: NotifySpaceMembersOptions) => {
  try {
    const { data: space } = await supabase
      .from('spaces')
      .select('name')
      .eq('id', options.spaceId)
      .single();

    if (!space) return;

    // Get all space members
    const { data: members } = await supabase.rpc('get_space_members', {
      p_space_id: options.spaceId,
    });

    if (!members || members.length === 0) return;

    // Send notification to each member (except the actor)
    for (const member of members) {
      if (member.user_id === options.actorId) continue;

      // Get member's preferences (select all notification fields)
      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('email_enabled, email_on_event_created, email_on_event_updated, email_on_event_deleted, email_on_member_joined, push_enabled, push_on_event_created, push_on_event_updated, push_on_event_deleted, push_on_member_joined')
        .eq('user_id', member.user_id)
        .single();

      // Fall back to all-enabled defaults if no preferences row exists yet
      const p = prefs ?? {
        email_enabled: true,
        email_on_event_created: true,
        email_on_event_updated: true,
        email_on_event_deleted: true,
        email_on_member_joined: true,
        push_enabled: true,
        push_on_event_created: true,
        push_on_event_updated: true,
        push_on_event_deleted: true,
        push_on_member_joined: true,
      };

      // Determine notification type
      let wantsEmail = false;
      let wantsPush = false;
      const pushTitle = getPushTitle(options.type, options.actorName);
      const pushBody = options.eventTitle || options.taskTitle || space.name;

      switch (options.type) {
        case 'event_created':
          wantsEmail = p.email_enabled && p.email_on_event_created;
          wantsPush = p.push_enabled && p.push_on_event_created;
          break;
        case 'event_updated':
          wantsEmail = p.email_enabled && p.email_on_event_updated;
          wantsPush = p.push_enabled && p.push_on_event_updated;
          break;
        case 'event_deleted':
          wantsEmail = p.email_enabled && p.email_on_event_deleted;
          wantsPush = p.push_enabled && p.push_on_event_deleted;
          break;
        case 'member_joined':
          wantsEmail = p.email_enabled && p.email_on_member_joined;
          wantsPush = p.push_enabled && p.push_on_member_joined;
          break;
      }

      // Send email
      if (wantsEmail) {
        await sendNotificationEmail({
          type: options.type as any,
          recipientEmail: member.email,
          recipientName: member.display_name,
          actorName: options.actorName,
          spaceName: space.name,
          eventTitle: options.eventTitle,
          taskTitle: options.taskTitle,
        });
      }

      // Send push
      if (wantsPush) {
        await sendPushNotification(member.user_id, {
          title: pushTitle,
          body: pushBody,
          tag: `${options.type}-${options.spaceId}`,
        });
      }
    }
  } catch (err) {
    console.error('Failed to notify space members:', err);
  }
};

/**
 * Send a direct notification email to a user
 */
export const sendNotificationEmail = async (options: SendNotificationEmailOptions) => {
  try {
    const { data, error } = await supabase.functions.invoke('send-notification', {
      body: {
        type: options.type,
        recipientEmail: options.recipientEmail,
        recipientName: options.recipientName,
        actorName: options.actorName,
        spaceName: options.spaceName,
        eventTitle: options.eventTitle,
        taskTitle: options.taskTitle,
        oldRole: options.oldRole,
        newRole: options.newRole,
      },
    });

    if (error) {
      console.error('Failed to send notification email:', error);
    }
  } catch (err) {
    console.error('Error invoking send-notification function:', err);
  }
};

/**
 * Notify when a member is removed from a space
 */
export const notifyMemberRemoved = async (userEmail: string, spaceName: string) => {
  await sendNotificationEmail({
    type: 'member_removed',
    recipientEmail: userEmail,
    spaceName,
  });
};

/**
 * Notify when a member's role changes
 */
export const notifyRoleChanged = async (
  userEmail: string,
  spaceName: string,
  oldRole: string,
  newRole: string
) => {
  await sendNotificationEmail({
    type: 'role_changed',
    recipientEmail: userEmail,
    spaceName,
    oldRole,
    newRole,
  });
};

/**
 * Notify when a user is assigned a task (by userId — looks up email + preferences internally)
 */
export const notifyTaskAssigned = async (options: {
  assigneeId: string;
  actorId: string;
  actorName: string;
  spaceId: string;
  taskTitle: string;
}) => {
  if (options.assigneeId === options.actorId) return; // Don't notify self-assignment

  try {
    const { data: space } = await supabase
      .from('spaces')
      .select('name')
      .eq('id', options.spaceId)
      .single();

    if (!space) return;

    // Get assignee email + preferences in one join
    const { data: prefs } = await supabase
      .from('notification_preferences')
      .select('push_enabled, push_on_task_assigned, email_enabled, email_on_task_assigned')
      .eq('user_id', options.assigneeId)
      .single();

    // Get assignee email from space members
    const { data: members } = await supabase.rpc('get_space_members', {
      p_space_id: options.spaceId,
    });

    const assignee = members?.find((m: any) => m.user_id === options.assigneeId);
    if (!assignee) return;

    if (prefs?.email_enabled && prefs?.email_on_task_assigned) {
      await sendNotificationEmail({
        type: 'task_assigned',
        recipientEmail: assignee.email,
        recipientName: assignee.display_name,
        actorName: options.actorName,
        spaceName: space.name,
        taskTitle: options.taskTitle,
      });
    }

    if (prefs?.push_enabled && prefs?.push_on_task_assigned) {
      await sendPushNotification(options.assigneeId, {
        title: `${options.actorName} assigned you a task`,
        body: options.taskTitle,
        tag: `task-assigned-${options.spaceId}`,
      });
    }
  } catch (err) {
    console.error('Failed to notify task assignee:', err);
  }
};

/**
 * Send push notification to a user
 */
const sendPushNotification = async (
  userId: string,
  options: { title: string; body: string; tag: string }
) => {
  try {
    const { error } = await supabase.functions.invoke('send-push', {
      body: {
        userId,
        title: options.title,
        body: options.body,
        tag: options.tag,
      },
    });

    if (error) {
      console.error('Failed to send push notification:', error);
    }
  } catch (err) {
    console.error('Error invoking send-push function:', err);
  }
};

/**
 * Get human-readable title for push notification
 */
function getPushTitle(type: string, actorName: string): string {
  switch (type) {
    case 'event_created':
      return `${actorName} created an event`;
    case 'event_updated':
      return `${actorName} updated an event`;
    case 'event_deleted':
      return `${actorName} deleted an event`;
    case 'member_joined':
      return `${actorName} joined the space`;
    default:
      return 'Notification';
  }
}
